import { captureEvent } from "@Light/app/analytics";
import { useAccount } from "@Light/components/page/account";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { PageBody } from "@Light/components/page/PageBody";
import { AddressLike, formatAddress } from "@Light/utils/address";
import { Lightning01, MarkerPin04 } from "@untitledui/icons";
import { AddressCard } from "../address/AddressCard";
import { ServiceLocationSheets } from "../address/ServiceLocationSheets";
import { useServiceLocationSheets } from "../address/useServiceLocationSheets";
import { useEnrollment } from "../flow/context";
import { requirementValue } from "../flow/screen";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { useIsAfterRate } from "../flow/useIsAfterRate";
import { EnrollRoute } from "../routes";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { useBrokerTermsAgreement } from "../useBrokerTermsAgreement";

export const ConfirmAddress = () => {
  const account = useAccount();
  const { next, requirements, back } = useEnrollment();
  const isAfterRate = useIsAfterRate();
  const stepper = useEnrollmentStepper(EnrollRoute.ConfirmAddress);

  const captureResponse = (event: string) => {
    const sourceAddress: AddressLike | undefined =
      account.locations?.[0] || account.preliminary_address;
    captureEvent(event, {
      source: account.locations?.[0] ? "location" : "preliminary",
      source_address: sourceAddress
        ? formatAddress(sourceAddress)
        : "[unknown]",
      confirm_address: formatAddress(address),
    });
  };

  const address = requirementValue(requirements.service_address);
  const brokerTermsAgreement = useBrokerTermsAgreement(!isAfterRate);
  const sheets = useServiceLocationSheets({
    postalCode: address.postal_code,
    additionalRequirements: brokerTermsAgreement,
  });

  const onConfirm = async () => {
    captureResponse("enroll_confirm_address_confirmed");
    await next(brokerTermsAgreement);
  };

  const onDecline = () => {
    captureResponse("enroll_confirm_address_declined");
    sheets.openSheet("address");
  };

  return (
    <PageBody
      stepper={stepper}
      centered
      icon={isAfterRate ? MarkerPin04 : Lightning01}
      title={
        isAfterRate ? (
          <>
            We just need a few details.
            <br />
            Is this the right address?
          </>
        ) : (
          "Let’s find your electricity plan. Is this the right address?"
        )
      }
      footer={
        <ButtonFooter>
          <Button
            size="lg"
            className="w-full"
            onClick={onConfirm}
            data-testid="confirm-address-yes"
          >
            {isAfterRate ? "Yes, continue" : "Yes, view offer"}
          </Button>
          <Button
            size="lg"
            buttonType="outline"
            className="w-full"
            onClick={onDecline}
            data-testid="confirm-address-no"
          >
            No, provide different address
          </Button>
          {!isAfterRate && <PartnerTosDisclaimer className="text-center" />}
        </ButtonFooter>
      }
      onBack={back}
    >
      <AddressCard address={address} data-testid="confirm-address-display" />
      <ServiceLocationSheets {...sheets} />
    </PageBody>
  );
};
